import moment from "moment";
import { useCallback } from "react";
import { ThirdPartyApiAccessEntity } from "entities/ThirdPartyApiAccess/ThirdPartyApiAccessEntity";
import { getUser } from "utilities/storageUtilities";
import { IApiAccessCorporation, IApiAccessKey } from "../ApiAccessOversightInterfaces";

/**
 * Enable/disable + per-key revoke for the MBO API-access surface, mirroring
 * useCorporationsLicenses. The acting osorno_administrator id (getUser, populated at
 * MBO login) is stamped on the audit row; it is nullable so a non-UI flip degrades
 * gracefully. The toggle-off cascade is the DB trigger's job — this hook never loops
 * over keys.
 */
const useApiAccessToggle = () => {
    const { enableApiAccessMutation, loading: enabling } = ThirdPartyApiAccessEntity.EnableApiAccessOsornoBO();
    const { disableApiAccessMutation, loading: disabling } = ThirdPartyApiAccessEntity.DisableApiAccessOsornoBO();
    const { revokeApiKeyMutation } = ThirdPartyApiAccessEntity.RevokeApiKeyOsornoBO();

    const getApiAccessStatus = useCallback(
        (row: IApiAccessCorporation): boolean => row.third_party_api_corporate_config?.is_enabled ?? false,
        [],
    );

    const enableApiAccess = useCallback(
        async (row: IApiAccessCorporation) => {
            await enableApiAccessMutation({
                variables: {
                    corporate_id: row.corporate_id,
                    changed_by_user_id: getUser() ?? null,
                },
            });
        },
        [enableApiAccessMutation],
    );

    const disableApiAccess = useCallback(
        async (row: IApiAccessCorporation) => {
            await disableApiAccessMutation({
                variables: {
                    corporate_id: row.corporate_id,
                    changed_by_user_id: getUser() ?? null,
                },
            });
        },
        [disableApiAccessMutation],
    );

    const revokeKey = useCallback(
        async (key: IApiAccessKey) => {
            await revokeApiKeyMutation({
                variables: {
                    third_party_api_key_id: key.third_party_api_key_id,
                    key_id_text: String(key.third_party_api_key_id),
                    corporate_id: key.corporate_id,
                    revoked_at: moment().toDate(),
                    changed_by_user_id: getUser() ?? null,
                },
            });
        },
        [revokeApiKeyMutation],
    );

    return { getApiAccessStatus, enableApiAccess, disableApiAccess, revokeKey, toggling: enabling || disabling };
};

export default useApiAccessToggle;
